/**
 * PATH: apps/quote/src/pages/pricelist-page/PivotTab.tsx
 * PURPOSE: Pivot table over the materials-from-quotes data — group by a chosen
 *          dimension (supplier / unit) and aggregate count, qty and est cost.
 * WHY: Operator 2026-06-14 — "Pivot tables generation."
 * FLOW: getMaterialTakeoff → groupBy(dimension) → rows of {key, count, qty, cost}
 *       + grand total. Pure client-side pivot; no new backend.
 * RELATED: pricing-api (getMaterialTakeoff), MaterialsTab
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Table2 } from 'lucide-react';
import { quoteApi } from '@/lib/quoteApi';
import { formatCurrency } from '@/lib/utils';

type Dimension = 'supplier' | 'unit';

interface PivotRow {
  key: string;
  count: number;
  qty: number;
  cost: number;
}

function pivot(materials: any[], dimension: Dimension): PivotRow[] {
  const map = new Map<string, PivotRow>();
  for (const m of materials) {
    const key = String(m[dimension] || '—').trim() || '—';
    const row = map.get(key) ?? { key, count: 0, qty: 0, cost: 0 };
    row.count += 1;
    row.qty += Number(m.qty) || 0;
    row.cost += Number(m.est_cost) || 0;
    map.set(key, row);
  }
  return [...map.values()]
    .map((r) => ({ ...r, qty: Math.round(r.qty * 100) / 100, cost: Math.round(r.cost * 100) / 100 }))
    .sort((a, b) => b.cost - a.cost);
}

export default function PivotTab() {
  const [dimension, setDimension] = useState<Dimension>('supplier');
  const { data, isLoading } = useQuery({
    queryKey: ['material-takeoff'],
    queryFn: () => quoteApi.getMaterialTakeoff(),
    staleTime: 60000,
  });

  const materials = data?.materials ?? [];
  const rows = useMemo(() => pivot(materials, dimension), [materials, dimension]);
  const totals = useMemo(
    () => rows.reduce((t, r) => ({ count: t.count + r.count, qty: t.qty + r.qty, cost: t.cost + r.cost }), { count: 0, qty: 0, cost: 0 }),
    [rows],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-gray-500 text-sm flex-1">Pivot the quote materials by a dimension to see spend distribution.</p>
        <label className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2">
          Group by
          <select
            value={dimension}
            onChange={(e) => setDimension(e.target.value as Dimension)}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
          >
            <option value="supplier">Supplier</option>
            <option value="unit">Unit</option>
          </select>
        </label>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : !rows.length ? (
          <div className="p-8 text-center text-gray-500">
            <Table2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            No quote materials to pivot yet.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{dimension}</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Materials</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Total qty</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Est. cost</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">% of spend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((r) => (
                <tr key={r.key} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-3 text-gray-900 dark:text-white">{r.key}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{r.count}</td>
                  <td className="px-6 py-3 text-right tabular-nums">{r.qty}</td>
                  <td className="px-6 py-3 text-right font-medium tabular-nums">{formatCurrency(r.cost)}</td>
                  <td className="px-6 py-3 text-right tabular-nums text-gray-500">
                    {totals.cost ? `${((r.cost / totals.cost) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <tr>
                <td className="px-6 py-3 text-gray-900 dark:text-white">Total</td>
                <td className="px-6 py-3 text-right tabular-nums">{totals.count}</td>
                <td className="px-6 py-3 text-right tabular-nums">{Math.round(totals.qty * 100) / 100}</td>
                <td className="px-6 py-3 text-right tabular-nums">{formatCurrency(totals.cost)}</td>
                <td className="px-6 py-3 text-right tabular-nums text-gray-500">100%</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
